import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useVocabGameSocket } from './useVocabGameSocket';
import { GameQuestion, TeamResult, FinalReport } from './vocabGameTypes';
import { playCorrectSound, playWrongSound, playTimerTick, playGameStart, playDone } from './soundEffects';

type Phase = 'enter_code' | 'enter_name' | 'waiting' | 'playing' | 'result' | 'finished';

const COLOR_MAP = ['#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6'];
const LABEL_MAP = ['A', 'B', 'C', 'D'];

export const VocabGameJoinPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState<Phase>('enter_code');
  const [code, setCode] = useState(searchParams.get('code')?.toUpperCase() || '');
  const [teamName, setTeamName] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [setName, setSetName] = useState('');
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questions, setQuestions] = useState<GameQuestion[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Array<{ questionIndex: number; answer: string; timeTakenMs: number }>>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(100); // percentage 100→0
  const [finalResult, setFinalResult] = useState<FinalReport | null>(null);
  const [teamResult, setTeamResult] = useState<TeamResult | null>(null);
  const [error, setError] = useState('');
  const [checkingRoom, setCheckingRoom] = useState(false);
  const tickedRef = useRef(false);

  const { joinRoom, submitAnswers, sendAnswerProgress } = useVocabGameSocket({
    onJoinSuccess: (data) => {
      setSetName(data.setName);
      setTotalQuestions(data.totalQuestions);
      setPhase('waiting');
      setError('');
    },
    onStudentGameStarted: (data) => {
      setQuestions(data.questions);
      setTotalQuestions(data.totalQuestions);
      setCurrentIdx(0);
      setAnswers([]);
      setPhase('playing');
      setQuestionStartTime(Date.now());
      setTimeLeft(100);
      playGameStart();
    },
    onTeamResult: (data) => {
      setTeamResult(data);
      setPhase('result');
      playDone();
    },
    onFinalResult: (data) => {
      setFinalResult(data);
      setPhase('finished');
    },
    onError: (msg) => {
      setError(msg);
      setCheckingRoom(false);
    },
  });

  // Countdown timer per question (30s)
  useEffect(() => {
    if (phase !== 'playing' || selectedOption !== null) return;
    const TOTAL_MS = 30000;
    const interval = setInterval(() => {
      const elapsed = Date.now() - questionStartTime;
      const pct = Math.max(0, 100 - (elapsed / TOTAL_MS) * 100);
      setTimeLeft(pct);
      // Tick sound when < 30% time left
      if (pct < 30 && pct > 0) { playTimerTick(); }
      if (pct <= 0) { handleAnswer(''); }
    }, 500);
    return () => clearInterval(interval);
  }, [phase, questionStartTime, selectedOption, currentIdx]);

  const handleCheckRoom = async () => {
    const trimCode = code.trim();
    if (trimCode.length !== 6) { setError('Mã phòng gồm 6 chữ số'); return; }
    setCheckingRoom(true);
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const res = await fetch(`${apiUrl}/api/vocab-game/room/${trimCode}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Không tìm thấy phòng');
        setCheckingRoom(false);
        return;
      }
      setCode(trimCode);
      setSetName(data.data.setName || '');
      setPhase('enter_name');
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại');
    } finally {
      setCheckingRoom(false);
    }
  };

  const handleJoin = () => {
    const name = teamName.trim();
    if (!name) { setError('Vui lòng nhập tên đội'); return; }
    joinRoom(code, name, avatar || undefined);
    setError('');
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch { setError('Không thể mở camera. Vui lòng kiểm tra quyền truy cập.'); }
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const size = Math.min(v.videoWidth, v.videoHeight);
    c.width = 150; c.height = 150;
    const ctx2d = c.getContext('2d')!;
    const ox = (v.videoWidth - size) / 2;
    const oy = (v.videoHeight - size) / 2;
    ctx2d.drawImage(v, ox, oy, size, size, 0, 0, 150, 150);
    setAvatar(c.toDataURL('image/jpeg', 0.7));
    closeCamera();
  };

  const handlePlayAgain = () => {
    setPhase('enter_code');
    setCode('');
    setTeamName('');
    setAvatar(null);
    setSetName('');
    setQuestions([]);
    setAnswers([]);
    setSelectedOption(null);
    setTeamResult(null);
    setFinalResult(null);
    setError('');
  };

  const handleAnswer = (option: string) => {

    if (selectedOption !== null && selectedOption !== '') return;
    const timeTakenMs = Date.now() - questionStartTime;
    setSelectedOption(option);
    // Play feedback sound (we don't know correct/wrong client-side — play neutral chime)
    if (option) playCorrectSound(); else playWrongSound(); // optimistic: any answer = ding, timeout = buzz
    sendAnswerProgress(code, currentIdx, option, timeTakenMs);
    const newAnswer = { questionIndex: currentIdx, answer: option, timeTakenMs };
    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);
    setTimeout(() => {
      const nextIdx = currentIdx + 1;
      if (nextIdx < questions.length) {
        setCurrentIdx(nextIdx);
        setSelectedOption(null);
        setQuestionStartTime(Date.now());
        setTimeLeft(100);
      } else {
        submitAnswers(code, newAnswers);
      }
    }, 700);
  };

  const currentQ = questions[currentIdx];

  // ── ENTER CODE ─────────────────────────────────────────────────────────────
  if (phase === 'enter_code') {
    return (
      <MobilePage>
        <GameLogo>🎮</GameLogo>
        <GameTitle>Tham gia Game</GameTitle>
        <GameSub>Nhập mã phòng từ giáo viên</GameSub>
        <CodeInput
          placeholder="123456"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={e => e.key === 'Enter' && handleCheckRoom()}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
        />
        {error && <ErrorMsg>{error}</ErrorMsg>}
        <BigBtn onClick={handleCheckRoom} disabled={checkingRoom}>
          {checkingRoom ? '⏳ Đang kiểm tra...' : 'Tìm phòng →'}
        </BigBtn>
      </MobilePage>
    );
  }

  // ── ENTER NAME ─────────────────────────────────────────────────────────────
  if (phase === 'enter_name') {
    return (
      <MobilePage>
        <CodeBadge>{code}</CodeBadge>
        <GameTitle>Đặt tên đội</GameTitle>
        {setName && <GameSub>📚 {setName}</GameSub>}

        {/* Avatar preview */}
        <AvatarCircle $src={avatar}>
          {!avatar && <span>{teamName ? teamName[0]?.toUpperCase() : '?'}</span>}
          {avatar && <img src={avatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />}
        </AvatarCircle>
        <CameraBtn onClick={openCamera}>📷 {avatar ? 'Chụp lại' : 'Chụp ảnh đội'}</CameraBtn>
        {avatar && <RemoveAvatarBtn onClick={() => setAvatar(null)}>✕ Xoá ảnh</RemoveAvatarBtn>}

        <TextInput
          placeholder="Tên đội của bạn..."
          value={teamName}
          onChange={e => setTeamName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleJoin()}
          maxLength={30}
          autoFocus
        />
        {error && <ErrorMsg>{error}</ErrorMsg>}
        <BigBtn onClick={handleJoin}>Vào phòng 🚀</BigBtn>
        <BackBtn onClick={() => setPhase('enter_code')}>← Đổi mã</BackBtn>

        {/* Camera overlay */}
        {cameraOpen && (
          <CameraOverlay>
            <CameraModal>
              <video ref={videoRef} autoPlay playsInline style={{ width: '100%', borderRadius: 12 }} />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <CameraActions>
                <CaptureBtn onClick={capturePhoto}>📸 Chụp</CaptureBtn>
                <CloseBtn onClick={closeCamera}>✕ Đóng</CloseBtn>
              </CameraActions>
            </CameraModal>
          </CameraOverlay>
        )}
      </MobilePage>
    );
  }

  // ── WAITING ─────────────────────────────────────────────────────────────────
  if (phase === 'waiting') {
    return (
      <MobilePage>
        <WaitingIcon>⏳</WaitingIcon>
        <WaitingTeam>{teamName}</WaitingTeam>
        <WaitingText>Đã vào phòng thành công!</WaitingText>
        <WaitingText style={{ color: '#64748b', fontSize: 14 }}>Chờ giáo viên bắt đầu...</WaitingText>
        {setName && <SetNameBadge>📚 {setName} • {totalQuestions} câu</SetNameBadge>}
        <PulseDot />
      </MobilePage>
    );
  }

  // ── PLAYING ─────────────────────────────────────────────────────────────────
  if (phase === 'playing' && currentQ) {
    return (
      <PlayPage>
        <PlayHeader>
          <ProgressText>{currentIdx + 1} / {questions.length}</ProgressText>
          <TimerBar>
            <TimerFill $pct={timeLeft} />
          </TimerBar>
          <ScoreText>{answers.reduce((s) => s, 0)}</ScoreText>
        </PlayHeader>
        <QuestionBox>
          <DirectionHint>
            {currentQ.direction === 'en_to_vi' ? '🇬🇧 → 🇻🇳' : '🇻🇳 → 🇬🇧'}
          </DirectionHint>
          <QuestionText>{currentQ.questionText}</QuestionText>
        </QuestionBox>
        <OptionsGrid>
          {currentQ.options.map((opt, i) => (
            <OptionButton
              key={i}
              $color={COLOR_MAP[i]}
              $selected={selectedOption === opt}
              $disabled={selectedOption !== null}
              onClick={() => handleAnswer(opt)}
            >
              <OptionText>{opt}</OptionText>
            </OptionButton>
          ))}
        </OptionsGrid>
        {selectedOption !== null && (
          <SelectedMsg>
            {selectedOption ? `✓ Đã chọn: ${selectedOption}` : '⏰ Hết giờ!'}
          </SelectedMsg>
        )}
      </PlayPage>
    );
  }

  // ── RESULT (personal) ───────────────────────────────────────────────────────
  if (phase === 'result' && teamResult) {
    return (
      <MobilePage>
        <ResultEmoji>{teamResult.rank <= 3 ? ['🥇','🥈','🥉'][teamResult.rank - 1] : '🎯'}</ResultEmoji>
        <ResultScore>{teamResult.score.toLocaleString()}</ResultScore>
        <ResultScoreLabel>điểm</ResultScoreLabel>
        <ResultStats>
          <StatBox $color="#22c55e">
            <StatVal>{teamResult.correctCount}</StatVal>
            <StatLbl>Đúng</StatLbl>
          </StatBox>
          <StatBox $color="#ef4444">
            <StatVal>{teamResult.wrongCount}</StatVal>
            <StatLbl>Sai</StatLbl>
          </StatBox>
          <StatBox $color="#8b5cf6">
            <StatVal>#{teamResult.rank}</StatVal>
            <StatLbl>Xếp hạng</StatLbl>
          </StatBox>
        </ResultStats>
        <WaitingText style={{ color: '#64748b', fontSize: 14 }}>Chờ kết quả chung cuộc...</WaitingText>
        <PulseDot />
        <PlayAgainBtn onClick={handlePlayAgain}>🎮 Chơi trò mới</PlayAgainBtn>
      </MobilePage>
    );
  }

  // ── FINISHED ────────────────────────────────────────────────────────────────
  if (phase === 'finished' && finalResult) {
    const myRank = teamResult?.rank ?? '-';
    return (
      <MobilePage>
        <ResultEmoji style={{ fontSize: 48 }}>🏁</ResultEmoji>
        <GameTitle>Kết thúc!</GameTitle>
        <SetNameBadge>{finalResult.setName}</SetNameBadge>
        <FinalRankingList>
          {finalResult.ranking.slice(0, 5).map((t, i) => (
            <FinalRankRow key={t.socketId} $isMe={t.name === teamName}>
              <span>{['🥇','🥈','🥉'][i] || `#${i+1}`}</span>
              <span style={{ flex: 1 }}>{t.name}</span>
              <span style={{ fontWeight: 700, color: '#a78bfa' }}>{t.score.toLocaleString()}</span>
            </FinalRankRow>
          ))}
        </FinalRankingList>
        <MyFinalRank>Xếp hạng của bạn: #{myRank}</MyFinalRank>
        <PlayAgainBtn onClick={handlePlayAgain}>🎮 Chơi trò mới</PlayAgainBtn>
      </MobilePage>
    );
  }

  return null;
};

// ── ANIMATIONS ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:none; }`;
const pulseDot = keyframes`0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:0.5}`;
const flashCorrect = keyframes`0%{background:rgba(34,197,94,0.3)}100%{background:transparent}`;
const flashWrong = keyframes`0%{background:rgba(239,68,68,0.3)}100%{background:transparent}`;

// ── STYLED ────────────────────────────────────────────────────────────────────
const MobilePage = styled.div`
  min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: linear-gradient(135deg,#0f172a,#1e1b4b); padding: 24px; font-family: 'Inter','Segoe UI',sans-serif; color: white;
`;
const GameLogo = styled.div`font-size: 64px; margin-bottom: 8px;`;
const GameTitle = styled.h1`font-size: 28px; font-weight: 900; margin: 0 0 4px; text-align: center;`;
const GameSub = styled.p`font-size: 14px; color: #94a3b8; margin: 0 0 24px; text-align: center;`;
const CodeInput = styled.input`
  width: 100%; max-width: 300px; padding: 16px; border-radius: 14px; border: 2px solid rgba(139,92,246,0.5);
  background: rgba(255,255,255,0.06); color: white; font-size: 28px; font-weight: 900; text-align: center;
  letter-spacing: 8px; margin-bottom: 16px; outline: none;
  &:focus { border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139,92,246,0.2); }
`;
const TextInput = styled.input`
  width: 100%; max-width: 320px; padding: 14px 18px; border-radius: 12px; border: 2px solid rgba(139,92,246,0.4);
  background: rgba(255,255,255,0.06); color: white; font-size: 16px; font-weight: 600; margin-bottom: 16px; outline: none;
  &:focus { border-color: #8b5cf6; }
`;
const BigBtn = styled.button<{ disabled?: boolean }>`
  width: 100%; max-width: 320px; padding: 16px; border-radius: 14px; border: none;
  background: ${p => p.disabled ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg,#7c3aed,#4f46e5)'}; 
  color: white; font-size: 18px; font-weight: 800; cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.25s; margin-bottom: 12px;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(124,58,237,0.5); }
`;
const BackBtn = styled.button`background:none;border:none;color:#64748b;cursor:pointer;font-size:14px;&:hover{color:#94a3b8}`;
const ErrorMsg = styled.div`background:rgba(239,68,68,0.15);border:1px solid #ef4444;border-radius:8px;padding:10px 14px;font-size:13px;color:#fca5a5;margin-bottom:12px;max-width:320px;text-align:center;`;
const CodeBadge = styled.div`background:rgba(139,92,246,0.2);border:1px solid rgba(139,92,246,0.5);border-radius:20px;padding:4px 16px;font-size:13px;font-weight:700;color:#a78bfa;margin-bottom:16px;letter-spacing:4px;`;
const SetNameBadge = styled.div`background:rgba(255,255,255,0.06);border-radius:20px;padding:6px 16px;font-size:13px;color:#94a3b8;margin-top:8px;`;
const WaitingIcon = styled.div`font-size:48px;margin-bottom:8px;`;
const WaitingTeam = styled.div`font-size:24px;font-weight:800;margin-bottom:4px;`;
const WaitingText = styled.div`font-size:16px;color:#94a3b8;margin-bottom:8px;`;
const PulseDot = styled.div`width:12px;height:12px;border-radius:50%;background:#8b5cf6;margin:16px auto 0;animation:${pulseDot} 1.5s infinite;`;

// Playing
const PlayPage = styled.div`min-height:100vh;display:flex;flex-direction:column;background:#0f172a;font-family:'Inter','Segoe UI',sans-serif;color:white;`;
const PlayHeader = styled.div`display:flex;align-items:center;gap:8px;padding:12px 16px;background:rgba(255,255,255,0.05);`;
const ProgressText = styled.div`font-size:13px;font-weight:700;color:#94a3b8;white-space:nowrap;`;
const TimerBar = styled.div`flex:1;height:8px;border-radius:4px;background:rgba(255,255,255,0.1);overflow:hidden;`;
const TimerFill = styled.div<{ $pct: number }>`height:100%;width:${p => p.$pct}%;background:${p => p.$pct > 50 ? '#22c55e' : p.$pct > 25 ? '#f59e0b' : '#ef4444'};transition:width 0.2s linear;`;
const ScoreText = styled.div`font-size:12px;font-weight:700;color:#a78bfa;white-space:nowrap;`;
const QuestionBox = styled.div`flex:0 0 auto;padding:24px 20px 16px;text-align:center;`;
const DirectionHint = styled.div`font-size:13px;color:#64748b;margin-bottom:8px;`;
const QuestionText = styled.div`font-size:22px;font-weight:800;line-height:1.3;`;
const OptionsGrid = styled.div`flex:1;display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:12px 16px 24px;`;
const OptionButton = styled.button<{ $color: string; $selected: boolean; $disabled: boolean }>`
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;
  padding:14px 8px;border-radius:14px;border:none;cursor:${p => p.$disabled ? 'not-allowed' : 'pointer'};
  background:${p => p.$selected ? p.$color : `${p.$color}25`};border:2px solid ${p => p.$selected ? p.$color : `${p.$color}60`};
  color:white;font-weight:700;transition:all 0.15s;
  &:hover:not(:disabled) { background:${p => `${p.$color}40`}; transform:scale(1.02); }
  &:active:not(:disabled) { transform:scale(0.98); }
`;
const OptionText = styled.div`font-size: 26px; font-weight: 800; text-align: center; line-height: 1.3;`;
const SelectedMsg = styled.div`text-align:center;padding:8px;font-size:14px;color:#94a3b8;font-weight:600;`;

// Result
const ResultEmoji = styled.div`font-size:56px;margin-bottom:8px;`;
const ResultScore = styled.div`font-size:56px;font-weight:900;background:linear-gradient(90deg,#a78bfa,#60a5fa);-webkit-background-clip:text;-webkit-text-fill-color:transparent;`;
const ResultScoreLabel = styled.div`font-size:14px;color:#64748b;font-weight:600;margin-bottom:20px;`;
const ResultStats = styled.div`display:flex;gap:12px;margin-bottom:20px;`;
const StatBox = styled.div<{ $color: string }>`background:${p => `${p.$color}15`};border:1px solid ${p => `${p.$color}40`};border-radius:12px;padding:14px 20px;text-align:center;`;
const StatVal = styled.div`font-size:22px;font-weight:800;`;
const StatLbl = styled.div`font-size:11px;color:#64748b;font-weight:600;`;
const FinalRankingList = styled.div`width:100%;max-width:320px;display:flex;flex-direction:column;gap:6px;margin:16px 0;`;
const FinalRankRow = styled.div<{ $isMe: boolean }>`display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;background:${p => p.$isMe ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.05)'};border:1px solid ${p => p.$isMe ? 'rgba(139,92,246,0.5)' : 'transparent'};font-size:14px;font-weight:600;`;
const MyFinalRank = styled.div`font-size:18px;font-weight:800;color:#a78bfa;margin-top:8px;`;

// Camera
const AvatarCircle = styled.div<{ $src: string | null }>`
  width: 80px; height: 80px; border-radius: 50%; margin-bottom: 8px;
  background: ${p => p.$src ? 'transparent' : 'linear-gradient(135deg,#8b5cf6,#3b82f6)'};
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; font-weight: 800; color: white;
  border: 3px solid rgba(139,92,246,0.5); overflow: hidden;
`;
const CameraBtn = styled.button`background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.2);border-radius:20px;color:white;padding:6px 14px;font-size:13px;cursor:pointer;margin-bottom:4px;transition:all 0.2s;&:hover{background:rgba(255,255,255,0.14)}`;
const RemoveAvatarBtn = styled.button`background:none;border:none;color:#64748b;font-size:12px;cursor:pointer;margin-bottom:8px;`;
const CameraOverlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:100;display:flex;align-items:center;justify-content:center;`;
const CameraModal = styled.div`background:#1e293b;border-radius:16px;padding:20px;width:90%;max-width:380px;`;
const CameraActions = styled.div`display:flex;gap:10px;margin-top:14px;`;
const CaptureBtn = styled.button`flex:1;padding:14px;border-radius:10px;border:none;background:linear-gradient(135deg,#7c3aed,#4f46e5);color:white;font-weight:800;font-size:16px;cursor:pointer;`;
const CloseBtn = styled.button`padding:14px 20px;border-radius:10px;border:1px solid rgba(255,255,255,0.2);background:transparent;color:#94a3b8;font-weight:700;cursor:pointer;`;

const PlayAgainBtn = styled.button`
  width: 100%; max-width: 320px; padding: 14px; border-radius: 14px; border: none;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white; font-size: 16px; font-weight: 800; cursor: pointer;
  transition: all 0.25s; margin-top: 20px;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(34,197,94,0.4); }
`;

export default VocabGameJoinPage;

